import { parseISO, format } from 'date-fns';
import compact from 'lodash/compact';

import empty from '#client/utils/empty';

const LOCALIZED_TEXT = {
  registeredAgents: 'businessProfile.registeredAgents',
  registeredAgentDetailsAwaited: 'businessProfile.registeredAgentDetailsAwaited',
  formationDate: 'businessProfile.formationDate',
  registrationState: 'businessProfile.registrationState',
  ein: 'businessProfile.ein',
  principalAddress: 'businessProfile.principalAddress',
  mailingAddress: 'businessProfile.mailingAddress',
  none: 'businessProfile.none',
};

const formatValueOrNone = (value, fm) => value || fm({ id: LOCALIZED_TEXT.none });

const formatLocationLine = ({ city, state, zip }) => compact([city ? `${city},` : '', state, zip]).join(' ');

const formatAddressLine = ({ line1, line2 }) => compact([line1, line2]).join(' ');

/**
 * Prefers a physical address; otherwise the first entry.
 *
 * @param {Array<{ type?: string }>|null|undefined} addresses
 * @returns {object|null}
 */
const pickDisplayAddress = (addresses) => {
  if (!Array.isArray(addresses) || addresses.length === 0) {
    return null;
  }

  return addresses.find((address) => address.type === 'physical') || addresses[0];
};

/**
 * Formats a registered-agent contact into an array of display lines for the business profile.
 *
 * @param {object} params
 * @param {object | null} params.contact - The RA contact object.
 * @param {string | null} [params.contact.fullName] - Display name for the agent (company or person).
 * @param {Array<object>} [params.contact.addresses] - Nested contact addresses (physical preferred).
 * @param {Function} fm - Localization function that takes an object with an `id` property.
 * @returns {string[]|string} An array of formatted lines, or a localized "none" string if no contact.
 *
 * @example
 * const contact = {
 *   fullName: 'Test Company Inc',
 *   addresses: [{
 *     type: 'physical',
 *     addressLine1: '123 Main St',
 *     addressLine2: 'Suite 100',
 *     city: 'Test City',
 *     state: 'CA',
 *     zip: '90210'
 *   }]
 * };
 *
 * const result = formatContact({ contact }, fm);
 *
 * // Returns: ['Test Company Inc', '123 Main St Suite 100', 'Test City, CA 90210']
 */

export const formatContact = ({ contact }, fm) => {
  if (!contact) {
    return fm({ id: LOCALIZED_TEXT.none });
  }

  const { fullName, addresses } = contact;
  const address = pickDisplayAddress(addresses);
  const { addressLine1, addressLine2, city, state, zip } = address || {};

  return compact([
    fullName,
    formatAddressLine({ line1: addressLine1, line2: addressLine2 }),
    formatLocationLine({ city, state, zip }),
  ]);
};

export const formatAddress = (address, fm) => {
  if (!address || !address.address1) {
    return fm({ id: LOCALIZED_TEXT.none });
  }

  const { address1, address2, city, state, zip } = address;

  return compact([formatAddressLine({ line1: address1, line2: address2 }), formatLocationLine({ city, state, zip })]);
};

export const formatEIN = (ein, fm) => {
  if (!ein) {
    return fm({ id: LOCALIZED_TEXT.none });
  }

  if (/^\d{9}$/.test(ein)) {
    return ein.replace(/^(\d{2})(\d{7})$/, '$1-$2');
  }

  return ein;
};

export const getInfos = ({
  principal,
  mailing,
  formationDate,
  domesticJurisdiction,
  registeredAgents,
  registeredAgentsError,
  ein,
  fm,
}) => [
  {
    title: fm({ id: LOCALIZED_TEXT.formationDate }),
    items: [
      {
        content: formationDate ? format(parseISO(formationDate), 'MM/dd/yyyy') : fm({ id: LOCALIZED_TEXT.none }),
      },
    ],
  },
  {
    title: fm({ id: LOCALIZED_TEXT.registrationState }),
    items: [{ content: formatValueOrNone(domesticJurisdiction, fm) }],
  },
  {
    title: fm({ id: LOCALIZED_TEXT.registeredAgents }),
    items: registeredAgents?.length
      ? registeredAgents.map(({ contact }) => ({
          // RA assigned but its contact hasn't been resolved yet -> show a pending message
          // rather than "None", which would read as "no registered agent".
          content: empty(contact)
            ? fm({ id: LOCALIZED_TEXT.registeredAgentDetailsAwaited })
            : formatContact({ contact }, fm),
        }))
      : [
          {
            content: registeredAgentsError
              ? fm({ id: LOCALIZED_TEXT.registeredAgentDetailsAwaited })
              : fm({ id: LOCALIZED_TEXT.none }),
          },
        ],
  },
  {
    title: fm({ id: LOCALIZED_TEXT.principalAddress }),
    items: [{ content: formatAddress(principal, fm) }],
  },
  {
    title: fm({ id: LOCALIZED_TEXT.mailingAddress }),
    items: [{ content: formatAddress(mailing, fm) }],
  },
  {
    title: fm({ id: LOCALIZED_TEXT.ein }),
    items: [{ content: formatEIN(ein, fm) }],
  },
];
